import { apiRequest, ApiError } from '@/lib/api/client';
import { withResolvedAvatarUrl } from '@/lib/api/avatar-url';
import { getAuthToken } from '@/lib/api/token-store';

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || '').trim();
  if (!normalizedToken) {
    throw new ApiError('authorization token is required', 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

function normalizeTask(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id || '').trim();
  if (!id) return null;
  return {
    id,
    threadId: String(raw.threadId || '').trim(),
    title: String(raw.title || '').trim(),
    status: raw.status === 'done' ? 'done' : 'todo',
    source: ['auto', 'ai', 'staff'].includes(raw.source) ? raw.source : 'staff',
    origin: String(raw.origin || '').trim(),
    assigneeId: String(raw.assigneeId || '').trim(),
    assignee: raw.assignee && typeof raw.assignee === 'object' ? raw.assignee : null,
    dueAt: raw.dueAt || null,
    createdAt: raw.createdAt || null,
    createdByUserId: String(raw.createdByUserId || '').trim(),
    createdByKind: String(raw.createdByKind || '').trim(),
    createdByName: String(raw.createdByName || '').trim(),
    doneAt: raw.doneAt || null,
    doneByName: String(raw.doneByName || '').trim(),
    updatedAt: raw.updatedAt || null,
    // Team Tasks page rows embed the enriched thread for the linked-thread chip.
    thread: raw.thread && typeof raw.thread === 'object' ? withResolvedAvatarUrl(raw.thread) : null,
  };
}

function normalizeTaskList(raw) {
  return (Array.isArray(raw) ? raw : []).map(normalizeTask).filter(Boolean);
}

/**
 * List one thread's checklist tasks (oldest first).
 * GET /api/threads/{threadId}/tasks
 */
export async function fetchThreadTasks({ token, threadId } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const payload = await apiRequest(`/api/threads/${encodeURIComponent(normalizedThreadId)}/tasks`, {
    method: 'GET',
    headers: createAuthHeaders(token),
  });
  return {
    threadId: String(payload?.threadId || normalizedThreadId).trim(),
    tasks: normalizeTaskList(payload?.tasks),
    raw: payload,
  };
}

/**
 * Add a staff task to a thread's checklist.
 * POST /api/threads/{threadId}/tasks  body { title, dueAt?, assigneeId? }
 */
export async function createThreadTask({ token, threadId, title, dueAt, assigneeId } = {}) {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new ApiError('threadId is required', 400);
  }
  const normalizedTitle = String(title || '').trim();
  if (!normalizedTitle) {
    throw new ApiError('title is required', 400);
  }
  const body = { title: normalizedTitle };
  const normalizedDueAt = dueAt instanceof Date ? dueAt.toISOString() : String(dueAt || '').trim();
  if (normalizedDueAt) body.dueAt = normalizedDueAt;
  const normalizedAssigneeId = String(assigneeId || '').trim();
  if (normalizedAssigneeId) body.assigneeId = normalizedAssigneeId;

  const payload = await apiRequest(`/api/threads/${encodeURIComponent(normalizedThreadId)}/tasks`, {
    method: 'POST',
    headers: {
      ...createAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  return { task: normalizeTask(payload?.task), raw: payload };
}

/**
 * Update a task: toggle done, claim/release, retitle, reschedule.
 * PATCH /api/tasks/{taskId}  body { status?, assigneeId?, reassign?, dueAt?, title? }
 *
 * Pass assigneeId "" to release. Setting a non-empty assigneeId is a claim — the server
 * rejects a lost race with 409 ("already claimed"); callers should refetch on that. Pass
 * `reassign: true` for a deliberate assign-to-someone action that may overwrite the current
 * assignee. Pass dueAt "" to clear the due date.
 */
export async function updateTask({ token, taskId, status, assigneeId, reassign, dueAt, title } = {}) {
  const normalizedTaskId = String(taskId || '').trim();
  if (!normalizedTaskId) {
    throw new ApiError('taskId is required', 400);
  }
  const body = {};
  if (status !== undefined) body.status = String(status || '').trim();
  if (assigneeId !== undefined) body.assigneeId = String(assigneeId || '').trim();
  if (reassign) body.reassign = true;
  if (title !== undefined) body.title = String(title || '').trim();
  if (dueAt !== undefined) {
    body.dueAt = dueAt instanceof Date ? dueAt.toISOString() : String(dueAt || '').trim();
  }

  const payload = await apiRequest(`/api/tasks/${encodeURIComponent(normalizedTaskId)}`, {
    method: 'PATCH',
    headers: {
      ...createAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  return { task: normalizeTask(payload?.task), raw: payload };
}

/**
 * Delete a task permanently.
 * DELETE /api/tasks/{taskId}
 */
export async function deleteTask({ token, taskId } = {}) {
  const normalizedTaskId = String(taskId || '').trim();
  if (!normalizedTaskId) {
    throw new ApiError('taskId is required', 400);
  }
  const payload = await apiRequest(`/api/tasks/${encodeURIComponent(normalizedTaskId)}`, {
    method: 'DELETE',
    headers: createAuthHeaders(token),
  });
  return { taskId: normalizedTaskId, deleted: Boolean(payload?.deleted), raw: payload };
}

/**
 * Team task queue: one smart view of every follow-up across the clinic. Each row embeds its
 * enriched thread for the linked-thread chip.
 * GET /api/tasks?view=unclaimed|mine|overdue|snoozed|doneToday&group=&assignee=&limit=
 */
export async function fetchTasks({ token, view, group, assignee, limit } = {}) {
  const params = new URLSearchParams();
  const normalizedView = String(view || '').trim();
  if (normalizedView) params.set('view', normalizedView);
  const normalizedGroup = String(group || '').trim();
  if (normalizedGroup) params.set('group', normalizedGroup);
  const normalizedAssignee = String(assignee || '').trim();
  if (normalizedAssignee) params.set('assignee', normalizedAssignee);
  if (Number.isFinite(limit) && limit > 0) params.set('limit', String(Math.floor(limit)));

  const qs = params.toString();
  const path = qs ? `/api/tasks?${qs}` : '/api/tasks';
  const payload = await apiRequest(path, {
    method: 'GET',
    headers: createAuthHeaders(token),
  });
  const rawPage = payload?.page && typeof payload.page === 'object' ? payload.page : {};
  return {
    tasks: normalizeTaskList(payload?.tasks),
    page: {
      returned: Number.isFinite(rawPage.returned) ? rawPage.returned : 0,
      limit: Number.isFinite(rawPage.limit) ? rawPage.limit : null,
    },
    raw: payload,
  };
}

/**
 * The clinic-wide working set for the task board and the Tasks list, merged from the smart views
 * the API exposes — there is no single "all tasks" endpoint. `unclaimed` + `mine` + `snoozed` +
 * `doneToday` cover the unassigned pool, your own work, and today's done; pass the teammate ids
 * (from /api/tasks/counts) to also pull each person's assigned tasks. Deduped by id, so callers
 * can classify lifecycle (overdue/snoozed/done) and ownership themselves instead of trusting a
 * single server view (whose snooze definition may differ from "has a future due date").
 */
export async function fetchClinicTasks({ token, assigneeIds = [] } = {}) {
  const ids = [...new Set((assigneeIds || []).map((x) => String(x || '').trim()).filter(Boolean))];
  const requests = [
    fetchTasks({ token, view: 'unclaimed' }),
    fetchTasks({ token, view: 'mine' }),
    fetchTasks({ token, view: 'snoozed' }),
    fetchTasks({ token, view: 'doneToday' }),
    ...ids.map((id) => fetchTasks({ token, assignee: id })),
  ];
  const settled = await Promise.allSettled(requests);
  const byId = new Map();
  let anyOk = false;
  for (const result of settled) {
    if (result.status !== 'fulfilled') continue;
    anyOk = true;
    for (const task of result.value.tasks) byId.set(task.id, task);
  }
  if (!anyOk) {
    const firstError = settled.find((r) => r.status === 'rejected');
    throw firstError ? firstError.reason : new ApiError('could not load tasks', 500);
  }
  return { tasks: [...byId.values()] };
}

/**
 * Smart-view counts for the Tasks page sidebar and the nav badge.
 * GET /api/tasks/counts → { unclaimed, mine, overdue, snoozed, doneToday, myOverdue,
 * byClinic: {groupId: n}, byTeammate: {userId: n} }
 */
export async function fetchTaskCounts({ token } = {}) {
  const payload = await apiRequest('/api/tasks/counts', {
    method: 'GET',
    headers: createAuthHeaders(token),
  });
  const num = (v) => (Number.isFinite(v) ? v : 0);
  return {
    unclaimed: num(payload?.unclaimed),
    mine: num(payload?.mine),
    overdue: num(payload?.overdue),
    snoozed: num(payload?.snoozed),
    doneToday: num(payload?.doneToday),
    myOverdue: num(payload?.myOverdue),
    byClinic: payload?.byClinic && typeof payload.byClinic === 'object' ? payload.byClinic : {},
    byTeammate: payload?.byTeammate && typeof payload.byTeammate === 'object' ? payload.byTeammate : {},
    // [{ id, displayName, count }] — byTeammate expanded with names for the sidebar.
    teammates: Array.isArray(payload?.teammates) ? payload.teammates : [],
    raw: payload,
  };
}
